import PropTypes from 'prop-types';
import React from 'react';

import { cssMap } from '@atlaskit/css';
import { Text, Stack } from '@atlaskit/primitives/compiled';
import Link from '@atlaskit/link';
import InlineMessage from '@atlaskit/inline-message';
import { SectionMessageAction } from '@atlaskit/section-message';

import { expVal } from '@confluence/feature-experiments';

import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys as storageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { useIsAdmin } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';

const DISMISS_STORAGE_KEY_PREFIX = storageKeys.CONNECT_EOS_BANNER_DISMISSED;
const SECONDS_PER_DAY = 24 * 60 * 60;
const MS_PER_DAY = SECONDS_PER_DAY * 1000;

function getDismissStorageKey(appKey) {
	return `${DISMISS_STORAGE_KEY_PREFIX}-${appKey}`;
}

function isDismissed(appKey) {
	// time when the popup was originally dismissed in milliseconds since epoch
	const dismissedAt = confluenceLocalStorageInstance.getItem(getDismissStorageKey(appKey));
	if (!dismissedAt) {
		return false; // we didn't previously dismiss it, or else the local storage expired
	}
	const dismissedTimestamp = Number(dismissedAt);
	if (isNaN(dismissedTimestamp)) {
		return false;
	}
	return (
		Date.now() - dismissedTimestamp <
		expVal('customer-messaging-iframe-banner-dismiss-days', 'value', 30) * // default is 30 days
			MS_PER_DAY
	);
}

const textStyles = cssMap({
	root: {
		whiteSpace: 'wrap',
	},
});

export function ConnectEOSPopup({ appKey }) {
	const [dismissed, setDismissed] = React.useState(() => isDismissed(appKey));
	const isAdmin = useIsAdmin();

	const dismiss = () => {
		const dismissDurationMs =
			expVal('customer-messaging-iframe-banner-dismiss-days', 'value', 30) * // default is 30 days
			MS_PER_DAY;
		const key = getDismissStorageKey(appKey);
		const value = Date.now();
		const expiry = dismissDurationMs / 1000;
		confluenceLocalStorageInstance.setItem(
			key,
			value, // value in milliseconds
			expiry, // expiry in seconds
		);
		setDismissed(true);
	};

	if (dismissed) {
		return null;
	}
	return (
		<div data-testid="inline-banner">
			<InlineMessage appearance="warning">
				<Stack space="space.150">
					<Text as="p" xcss={textStyles.root}>
						{isAdmin ? (
							<>
								{
									"This app is running on a platform that'll be unsupported after December 2026. Check "
								}
								<Link href={`${CONTEXT_PATH}/plugins/servlet/upm`}>Connected apps</Link>
								{' to see what options are available for all similar apps.'}
							</>
						) : (
							"This app is running on a platform that'll be unsupported after December 2026. To explore your options, contact your site admin."
						)}
					</Text>
					<Text as="p" xcss={textStyles.root}>
						<SectionMessageAction onClick={dismiss} key="dismiss">
							Dismiss
						</SectionMessageAction>
					</Text>
				</Stack>
			</InlineMessage>
		</div>
	);
}

ConnectEOSPopup.propTypes = {
	appKey: PropTypes.string,
};
